import React, { useState, useRef, useEffect } from 'react';
import './ZoomableCanvas.css';

const ZoomableCanvas = () => {
  // Fixed dimensions
  const CLUSTER_WIDTH = 800;
  const CLUSTER_HEIGHT = 450;
  const UNIVERSE_WIDTH = 6000;
  const UNIVERSE_HEIGHT = 6000;

  // Cluster data with 7 clusters
  const clusters = [
    {
      id: 'DESIGN',
      position: { x: 500, y: 500 },
      color: '#6366f1',
      radius: 500,
      cards: Array.from({ length: 12 }, (_, i) => ({
        id: `design-card-${i+1}`,
        title: `Design Principle ${i+1}`,
        content: 'Fundamental rules for creating effective user interfaces.',
        tags: ['UI', 'UX', 'Color', 'Typography', 'Layout'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'TECHNOLOGY',
      position: { x: 2500, y: 1200 },
      color: '#10b981',
      radius: 500,
      cards: Array.from({ length: 15 }, (_, i) => ({
        id: `tech-card-${i+1}`,
        title: `Tech Innovation ${i+1}`,
        content: 'Emerging technologies shaping digital experiences.',
        tags: ['AI', 'Blockchain', 'IoT', 'VR', 'Quantum'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'STRATEGY',
      position: { x: 1500, y: 3000 },
      color: '#f59e0b',
      radius: 500,
      cards: Array.from({ length: 10 }, (_, i) => ({
        id: `strategy-card-${i+1}`,
        title: `Strategic Approach ${i+1}`,
        content: 'Methodologies for business growth.',
        tags: ['SWOT', 'PEST', 'OKR', 'KPI', 'ROI'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'MARKETING',
      position: { x: 3500, y: 500 },
      color: '#8b5cf6',
      radius: 500,
      cards: Array.from({ length: 8 }, (_, i) => ({
        id: `marketing-card-${i+1}`,
        title: `Marketing Campaign ${i+1}`,
        content: 'Strategies to promote products and services.',
        tags: ['SEO', 'Social', 'Content', 'Email', 'PPC'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'DEVELOPMENT',
      position: { x: 500, y: 4000 },
      color: '#ec4899',
      radius: 500,
      cards: Array.from({ length: 10 }, (_, i) => ({
        id: `dev-card-${i+1}`,
        title: `Development Method ${i+1}`,
        content: 'Software development practices and methodologies.',
        tags: ['Agile', 'Scrum', 'DevOps', 'CI/CD', 'TDD'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'ANALYTICS',
      position: { x: 4500, y: 3000 },
      color: '#3b82f6',
      radius: 500,
      cards: Array.from({ length: 7 }, (_, i) => ({
        id: `analytics-card-${i+1}`,
        title: `Analytics Insight ${i+1}`,
        content: 'Data analysis techniques and business intelligence.',
        tags: ['Data', 'BI', 'Reports', 'Dashboards', 'KPIs'].slice(0, 2 + i % 3)
      }))
    },
    {
      id: 'LEADERSHIP',
      position: { x: 3000, y: 4500 },
      color: '#ef4444',
      radius: 500,
      cards: Array.from({ length: 9 }, (_, i) => ({
        id: `leadership-card-${i+1}`,
        title: `Leadership Quality ${i+1}`,
        content: 'Traits and practices of effective leaders.',
        tags: ['Vision', 'Team', 'Communication', 'Decision', 'Empathy'].slice(0, 2 + i % 3)
      }))
    }
  ];

  // Connections
  const connections = [
    { from: 'DESIGN', to: 'TECHNOLOGY', color: 'rgba(99, 102, 241, 0.3)' },
    { from: 'DESIGN', to: 'STRATEGY', color: 'rgba(99, 102, 241, 0.3)' },
    { from: 'TECHNOLOGY', to: 'STRATEGY', color: 'rgba(16, 185, 129, 0.3)' },
    { from: 'MARKETING', to: 'DESIGN', color: 'rgba(139, 92, 246, 0.3)' },
    { from: 'DEVELOPMENT', to: 'TECHNOLOGY', color: 'rgba(236, 72, 153, 0.3)' },
    { from: 'ANALYTICS', to: 'STRATEGY', color: 'rgba(59, 130, 246, 0.3)' },
    { from: 'LEADERSHIP', to: 'STRATEGY', color: 'rgba(239, 68, 68, 0.3)' },
    { from: 'LEADERSHIP', to: 'DEVELOPMENT', color: 'rgba(239, 68, 68, 0.3)' },
    { from: 'ANALYTICS', to: 'TECHNOLOGY', color: 'rgba(59, 130, 246, 0.3)' }
  ];

  // State
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startDragPos, setStartDragPos] = useState({ x: 0, y: 0 });
  const [visitors, setVisitors] = useState(
    Array.from({ length: 12 }, (_, i) => createVisitor(clusters[i % clusters.length], i))
  );
  const [userPosition, setUserPosition] = useState({ x: 100, y: 100 });
  const [hasUserMoved, setHasUserMoved] = useState(false);
  const [collisions, setCollisions] = useState({});
  const canvasRef = useRef(null);
  const autoPanRef = useRef(null);
  const visitorsRef = useRef(visitors);
  const userPositionRef = useRef(userPosition);

  // Update refs when state changes
  useEffect(() => {
    visitorsRef.current = visitors;
    userPositionRef.current = userPosition;
  }, [visitors, userPosition]);

  // Create visitor with avatar images
  function createVisitor(cluster, id) {
    const angle = Math.random() * Math.PI * 2;
    const pos = getPositionAtClusterEdge(cluster, angle);
    const visitorNames = ['Alex', 'Taylor', 'Jordan', 'Casey', 'Riley', 'Quinn', 'Morgan', 'Avery', 'Cameron', 'Jamie', 'Drew', 'Reese'];
    const gender = Math.random() > 0.5 ? 'boy' : 'girl';
    return {
      id: `visitor-${id}`,
      name: visitorNames[id % visitorNames.length],
      currentX: pos.x,
      currentY: pos.y,
      targetX: pos.x,
      targetY: pos.y,
      targetCluster: cluster.id,
      size: 50,
      moving: false,
      animationDuration: '15s',
      avatar: `https://avatar.iran.liara.run/public/${gender}?seed=${id}`,
      collided: false
    };
  }

  // Get edge position
  function getPositionAtClusterEdge(cluster, angle) {
    return {
      x: cluster.position.x + CLUSTER_WIDTH/2 + Math.cos(angle) * cluster.radius,
      y: cluster.position.y + CLUSTER_HEIGHT/2 + Math.sin(angle) * cluster.radius
    };
  }

  // Set new target
  function setNewTarget(visitor) {
    if (visitor.collided) return visitor;
    
    const currentCluster = clusters.find(c => c.id === visitor.targetCluster);
    let nextCluster;
    
    do {
      nextCluster = clusters[Math.floor(Math.random() * clusters.length)];
    } while (nextCluster.id === currentCluster.id && clusters.length > 1);
    
    const angle = Math.random() * Math.PI * 2;
    const targetPos = getPositionAtClusterEdge(nextCluster, angle);
    
    // Calculate distance for animation duration
    const dx = targetPos.x - visitor.currentX;
    const dy = targetPos.y - visitor.currentY;
    const distance = Math.sqrt(dx*dx + dy*dy);
    const duration = Math.max(10, Math.min(30, distance / 50)) + 's';
    
    return {
      ...visitor,
      targetX: targetPos.x,
      targetY: targetPos.y,
      targetCluster: nextCluster.id,
      moving: true,
      animationDuration: duration
    };
  }

  // Initialize visitors
  useEffect(() => {
    setVisitors(prev => prev.map(v => setNewTarget(v)));
    
    const interval = setInterval(() => {
      setVisitors(prev => prev.map(v => {
        return v.moving ? v : setNewTarget(v);
      }));
    }, 3000);
    
    return () => clearInterval(interval);
  }, []);

  // Handle animation end
  function handleAnimationEnd(id) {
    setVisitors(prev => prev.map(v => {
      if (v.id === id) {
        return {
          ...v,
          currentX: v.targetX,
          currentY: v.targetY,
          moving: false,
          collided: false
        };
      }
      return v;
    }));
  }

  // Collision detection
  useEffect(() => {
    if (!hasUserMoved) return;
    
    const collisionCheckInterval = setInterval(() => {
      const currentVisitors = visitorsRef.current;
      const currentUserPos = userPositionRef.current;
      const COLLISION_DISTANCE = 50;
      
      const collidedVisitors = [];
      const updatedVisitors = currentVisitors.map(visitor => {
        if (visitor.collided) return visitor;
        
        const dx = visitor.currentX - currentUserPos.x;
        const dy = visitor.currentY - currentUserPos.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < COLLISION_DISTANCE) {
          collidedVisitors.push(visitor);
          return {
            ...visitor,
            moving: false,
            collided: true
          };
        }
        return visitor;
      });
      
      if (collidedVisitors.length > 0) {
        setVisitors(updatedVisitors);
        setCollisions(prev => {
          const newCollisions = { ...prev };
          collidedVisitors.forEach(v => {
            newCollisions[v.id] = v;
          });
          return newCollisions;
        });
      }
    }, 100);
    
    return () => clearInterval(collisionCheckInterval);
  }, [hasUserMoved]);

  // Resolve collision
  const resolveCollision = (visitorId) => {
    setVisitors(prev => prev.map(v => 
      v.id === visitorId ? { ...v, collided: false } : v
    ));
    
    setCollisions(prev => {
      const newCollisions = { ...prev };
      delete newCollisions[visitorId];
      return newCollisions;
    });
  };

  // Keyboard controls for user sprite
  useEffect(() => {
    const handleKeyDown = (e) => {
      const speed = 20;
      
      // User sprite movement (WASD)
      if (e.key === 'a' || e.key === 'A' || e.key === 'd' || e.key === 'D' || 
          e.key === 'w' || e.key === 'W' || e.key === 's' || e.key === 'S') {
        setUserPosition(prev => {
          let newX = prev.x;
          let newY = prev.y;
          
          if (e.key === 'a' || e.key === 'A') newX -= speed;
          if (e.key === 'd' || e.key === 'D') newX += speed;
          if (e.key === 'w' || e.key === 'W') newY -= speed;
          if (e.key === 's' || e.key === 'S') newY += speed;
          
          // Mark user movement
          if (!hasUserMoved) {
            setHasUserMoved(true);
            // Trigger zoom out after 3-4 seconds
            setTimeout(() => {
              // Smooth zoom to 64%
              setZoom(0.64);
              
              // Center view on user
              if (canvasRef.current) {
                const canvasRect = canvasRef.current.getBoundingClientRect();
                setPosition({
                  x: canvasRect.width / 2 - userPosition.x * 0.64,
                  y: canvasRect.height / 2 - userPosition.y * 0.64
                });
              }
            }, 3500);
          }
          
          return { x: newX, y: newY };
        });
      }
      
      // Viewport movement (Arrow keys)
      const viewportSpeed = 30;
      if (e.key === 'ArrowLeft') {
        setPosition(prev => ({ ...prev, x: prev.x + viewportSpeed }));
      }
      if (e.key === 'ArrowRight') {
        setPosition(prev => ({ ...prev, x: prev.x - viewportSpeed }));
      }
      if (e.key === 'ArrowUp') {
        setPosition(prev => ({ ...prev, y: prev.y + viewportSpeed }));
      }
      if (e.key === 'ArrowDown') {
        setPosition(prev => ({ ...prev, y: prev.y - viewportSpeed }));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [hasUserMoved, userPosition]);

  // Auto-pan when user sprite is near edge
  useEffect(() => {
    if (!canvasRef.current || !hasUserMoved) return;
    
    const checkAutoPan = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const rect = canvas.getBoundingClientRect();
      const threshold = 200; // pixels from edge to trigger pan
      
      // Convert user position to screen coordinates
      const screenX = userPosition.x * zoom + position.x;
      const screenY = userPosition.y * zoom + position.y;
      
      let newPosition = { ...position };
      let needsUpdate = false;
      
      // Check left edge
      if (screenX < threshold) {
        newPosition.x = position.x + (threshold - screenX);
        needsUpdate = true;
      }
      // Check right edge
      else if (screenX > rect.width - threshold) {
        newPosition.x = position.x - (screenX - (rect.width - threshold));
        needsUpdate = true;
      }
      
      // Check top edge
      if (screenY < threshold) {
        newPosition.y = position.y + (threshold - screenY);
        needsUpdate = true;
      }
      // Check bottom edge
      else if (screenY > rect.height - threshold) {
        newPosition.y = position.y - (screenY - (rect.height - threshold));
        needsUpdate = true;
      }
      
      if (needsUpdate) {
        setPosition(newPosition);
      }
    };
    
    autoPanRef.current = setInterval(checkAutoPan, 100);
    return () => clearInterval(autoPanRef.current);
  }, [userPosition, zoom, position, hasUserMoved]);

  // Canvas interaction handlers
  const handleWheel = (e) => {
    if (e.target.closest('.cluster-cards')) return;
    e.preventDefault();
    
    const delta = e.deltaY * -0.001;
    const newZoom = Math.min(Math.max(0.1, zoom + delta), 5);
    
    const rect = canvasRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    const zoomPointX = (mouseX - position.x) / zoom;
    const zoomPointY = (mouseY - position.y) / zoom;
    
    setZoom(newZoom);
    setPosition({
      x: mouseX - zoomPointX * newZoom,
      y: mouseY - zoomPointY * newZoom
    });
  };

  const handleMouseDown = (e) => {
    if (e.target.closest('.cluster-cards') || e.button !== 0) return;
    setIsDragging(true);
    setStartDragPos({ x: e.clientX - position.x, y: e.clientY - position.y });
    document.body.style.cursor = 'grabbing';
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    setPosition({
      x: e.clientX - startDragPos.x,
      y: e.clientY - startDragPos.y
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.body.style.cursor = '';
  };

  const resetView = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const zoomIn = () => setZoom(prev => Math.min(prev * 1.2, 5));
  const zoomOut = () => setZoom(prev => Math.max(prev * 0.8, 0.1));

  // Connection paths with double 90-degree turns and rounded corners
  function getConnectionPath(fromClusterId, toClusterId) {
    const fromCluster = clusters.find(c => c.id === fromClusterId);
    const toCluster = clusters.find(c => c.id === toClusterId);
    if (!fromCluster || !toCluster) return '';
    
    const fromX = fromCluster.position.x + CLUSTER_WIDTH/2;
    const fromY = fromCluster.position.y + CLUSTER_HEIGHT/2;
    const toX = toCluster.position.x + CLUSTER_WIDTH/2;
    const toY = toCluster.position.y + CLUSTER_HEIGHT/2;
    
    // Calculate direction and distance
    const dx = toX - fromX;
    const dy = toY - fromY;
    const distance = Math.sqrt(dx*dx + dy*dy);
    
    // Determine turn points with rounded corners
    const turnRadius = Math.min(distance * 0.2, 150);
    const turnPoint1 = {
      x: fromX + (dx > 0 ? turnRadius : -turnRadius),
      y: fromY
    };
    const turnPoint2 = {
      x: turnPoint1.x,
      y: toY - (dy > 0 ? turnRadius : -turnRadius)
    };
    const turnPoint3 = {
      x: toX,
      y: turnPoint2.y
    };
    
    // Create path with rounded corners using quadratic curves
    return `
      M ${fromX},${fromY}
      L ${turnPoint1.x - (dx > 0 ? turnRadius/2 : -turnRadius/2)},${fromY}
      Q ${turnPoint1.x},${fromY} ${turnPoint1.x},${fromY + (dy > 0 ? turnRadius/2 : -turnRadius/2)}
      L ${turnPoint1.x},${turnPoint2.y - (dy > 0 ? turnRadius/2 : -turnRadius/2)}
      Q ${turnPoint1.x},${turnPoint2.y} ${turnPoint1.x + (dx > 0 ? turnRadius/2 : -turnRadius/2)},${turnPoint2.y}
      L ${turnPoint3.x - (dx > 0 ? turnRadius/2 : -turnRadius/2)},${turnPoint2.y}
      Q ${turnPoint3.x},${turnPoint2.y} ${turnPoint3.x},${turnPoint2.y + (dy > 0 ? turnRadius/2 : -turnRadius/2)}
      L ${toX},${toY}
    `;
  }

  // Map background
  const generateMapBackground = () => {
    return (
      <div className="map-background" style={{
        backgroundImage: 'linear-gradient(rgba(200, 200, 200, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(200, 200, 200, 0.2) 1px, transparent 1px)',
        backgroundSize: '50px 50px',
        width: '100%',
        height: '100%',
        position: 'absolute'
      }} />
    );
  };

  // Get optimal connection pill position
  function getConnectionPillPosition(fromClusterId, toClusterId) {
    const fromCluster = clusters.find(c => c.id === fromClusterId);
    const toCluster = clusters.find(c => c.id === toClusterId);
    if (!fromCluster || !toCluster) return { x: 0, y: 0 };
    
    const fromX = fromCluster.position.x + CLUSTER_WIDTH/2;
    const fromY = fromCluster.position.y + CLUSTER_HEIGHT/2;
    const toX = toCluster.position.x + CLUSTER_WIDTH/2;
    const toY = toCluster.position.y + CLUSTER_HEIGHT/2;
    
    // Calculate direction vector
    const dx = toX - fromX;
    const dy = toY - fromY;
    
    // Calculate position at 40% of the path
    const progress = 0.4;
    const midX = fromX + dx * progress;
    const midY = fromY + dy * progress;
    
    // Adjust position to be above the line
    const angle = Math.atan2(dy, dx);
    const offset = 30;
    const offsetX = -Math.sin(angle) * offset;
    const offsetY = Math.cos(angle) * offset;
    
    return { 
      x: midX + offsetX, 
      y: midY + offsetY 
    };
  }

  return (
    <div className="zoomable-canvas-container">
      <div className="zoom-controls">
        <button onClick={zoomIn}>Zoom In (+)</button>
        <button onClick={zoomOut}>Zoom Out (-)</button>
        <button onClick={resetView}>Reset View</button>
        <span>Zoom: {Math.round(zoom * 100)}%</span>
      </div>
      
      <div 
        className="zoomable-canvas"
        ref={canvasRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <div 
          className="canvas-content" 
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            width: `${UNIVERSE_WIDTH}px`,
            height: `${UNIVERSE_HEIGHT}px`,
            backgroundColor: '#f8f9fa'
          }}
        >
          {generateMapBackground()}
          
          <svg className="connections-layer" width={UNIVERSE_WIDTH} height={UNIVERSE_HEIGHT}>
            {connections.map((conn, i) => {
              const pillPos = getConnectionPillPosition(conn.from, conn.to);
              return (
                <React.Fragment key={i}>
                  <path
                    d={getConnectionPath(conn.from, conn.to)}
                    stroke={conn.color}
                    strokeWidth="2"
                    fill="none"
                    strokeDasharray="10,5"
                  />
                  {/* Connection direction pill */}
                  <g transform={`translate(${pillPos.x}, ${pillPos.y})`}>
                    <rect 
                      x="-35" 
                      y="-10" 
                      width="70" 
                      height="20" 
                      rx="10" 
                      fill="#333" 
                      filter="url(#pill-glow)"
                    />
                    <text 
                      textAnchor="middle" 
                      fill="white" 
                      fontSize="10px" 
                      dy="0.3em"
                      fontWeight="bold"
                    >
                      → {conn.to}
                    </text>
                  </g>
                </React.Fragment>
              );
            })}
            <defs>
              <filter id="pill-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
          </svg>
          
          {clusters.map((cluster) => (
            <div 
              key={cluster.id}
              className="cluster-container"
              style={{
                left: `${cluster.position.x}px`,
                top: `${cluster.position.y}px`,
                borderColor: cluster.color,
                width: `${CLUSTER_WIDTH}px`,
                height: `${CLUSTER_HEIGHT}px`
              }}
            >
              <div className="cluster-header" style={{ backgroundColor: cluster.color }}>
                <h3>{cluster.id}</h3>
              </div>
              <div className="cluster-cards">
                {cluster.cards.map((card) => (
                  <div key={card.id} className="card">
                    <div className="card-header" style={{ backgroundColor: cluster.color }}>
                      <div className="card-title">{card.title}</div>
                    </div>
                    <div className="card-image-placeholder">
                      <div className="color-block" style={{ backgroundColor: cluster.color }}></div>
                    </div>
                    <div className="card-content">
                      <p>{card.content}</p>
                      <div className="card-tags">
                        {card.tags.map((tag, i) => (
                          <span key={i} className="tag" style={{ backgroundColor: cluster.color }}>{tag}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          
          {/* Visitor sprites with avatar images */}
          {visitors.map(visitor => {
            const cluster = clusters.find(c => c.id === visitor.targetCluster);
            return (
              <div
                key={visitor.id}
                className={`visitor-container ${visitor.moving && !visitor.collided ? 'moving' : ''}`}
                style={{
                  '--start-x': `${visitor.currentX}px`,
                  '--start-y': `${visitor.currentY}px`,
                  '--end-x': `${visitor.targetX}px`,
                  '--end-y': `${visitor.targetY}px`,
                  '--size': `${visitor.size}px`,
                  '--duration': visitor.animationDuration,
                  left: `${visitor.currentX}px`,
                  top: `${visitor.currentY}px`,
                }}
                onAnimationEnd={() => handleAnimationEnd(visitor.id)}
              >
                <img 
                  src={visitor.avatar} 
                  alt="Visitor" 
                  className="visitor-sprite"
                  style={{ 
                    width: `${visitor.size}px`, 
                    height: `${visitor.size}px`,
                    borderColor: cluster?.color || '#000'
                  }} 
                />
                <div className="visitor-pill">
                  {visitor.name}
                </div>
              </div>
            );
          })}
          
          {/* User-controlled sprite */}
          <div
            className="visitor-container user-sprite"
            style={{
              left: `${userPosition.x}px`,
              top: `${userPosition.y}px`,
            }}
          >
            <img 
              src="https://avatar.iran.liara.run/public/boy?seed=user" 
              alt="User" 
              className="visitor-sprite"
              style={{ 
                width: '50px', 
                height: '50px',
                borderColor: '#000'
              }} 
            />
            <div className="visitor-pill">
              USER
            </div>
          </div>
          
          {/* Flying cards for collisions */}
          {Object.values(collisions).map(collision => {
            const visitor = collision;
            const cluster = clusters.find(c => c.id === visitor.targetCluster);
            
            return (
              <div 
                key={`collision-${visitor.id}`}
                className="flying-card"
                style={{
                  left: `${visitor.currentX}px`,
                  top: `${visitor.currentY - 120}px`,
                  borderColor: cluster?.color || '#000',
                  zIndex: 1000
                }}
              >
                <div className="card-header" style={{ backgroundColor: cluster?.color || '#000' }}>
                  <div className="card-title">{visitor.name}</div>
                  <button 
                    className="close-btn"
                    onClick={() => resolveCollision(visitor.id)}
                  >
                    ×
                  </button>
                </div>
                <div className="card-content">
                  <div className="avatar-container">
                    <img 
                      src={visitor.avatar} 
                      alt={visitor.name} 
                      className="avatar" 
                    />
                  </div>
                  <div className="info-section">
                    <p><strong>Current Cluster:</strong> {visitor.targetCluster}</p>
                    <p><strong>Status:</strong> Exploring</p>
                    <p><strong>Activity:</strong> Learning about {visitor.targetCluster} concepts</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="controls-info">
        Use <strong>W, A, S, D</strong> keys to move USER sprite | 
        Use <strong>Arrow keys</strong> to pan viewport
      </div>
    </div>
  );
};

export default ZoomableCanvas;